import {Occupancy} from "@variocube/app-api";
import {
    Avatar,
    PushButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableSort,
    typography
} from "@variocube/cube-ui";
import * as React from "react";
import {Fragment, useCallback, useMemo, useState} from "react";
import {compareDelivery, Delivery, DeliveryState, formatDateTime} from "./types";
import {findDeliveryOccupancies, shortenUuid} from "./utils";
import {stylesheet} from "typestyle";
import {useLocalization} from "../i18n";
import {DeliveryStateDisplay} from "./DeliveryStateDisplay";
import {DeliveryTagsDisplay} from "./DeliveryTagsDisplay";

interface DeliveryTableProps {
    deliveries: Delivery[];
    occupancies: Occupancy[];
    onOpen: (delivery: Delivery) => void;
    onCheck: (delivery: Delivery, boxNumber: string) => void;
    onOpenAll?: () => void;
}

export function DeliveryTable({ deliveries, occupancies, onOpen, onCheck, onOpenAll }: DeliveryTableProps) {
    const [sort, setSort] = useState<TableSort>({ column: 'deliveryId', order: 'asc' });
    const [checked, setChecked] = useState<string[]>([]);

    const { t } = useLocalization();

    const sorted = useMemo<Delivery[]>(() => {
        if (occupancies && deliveries) {
            if (!sort) {
                return deliveries;
            }
            return deliveries.sort((a, b) => compareDelivery(a, b, sort));
        }
        return [];
    }, [sort, deliveries, occupancies]);

    const numberOfColumns = useMemo<number>(() => {
        let columns = 6; // non-optional
        if (onOpenAll) {
            columns++;
        }
        return columns;
    }, [onOpenAll]);

    return (
        <Table sort={sort} onSort={setSort}>
            <TableHead>
                <TableRow>
                    <TableCell column="delivery">{t("Status")}</TableCell>
                    <TableCell column="delivery">{t("Delivery")}</TableCell>
                    <TableCell column="recipient">{t("Recipient")}</TableCell>
                    <TableCell column="storedAt">{t("StoredPickedUpAt")}</TableCell>
                    <TableCell column="box" colSpan={onOpenAll ? 1 : 2}>{t("Box")}</TableCell>
                    {onOpenAll &&
                        <TableCell>
                            <PushButton primary onClick={onOpenAll}> {t("OpenAll")} </PushButton>
                        </TableCell>
                    }
                </TableRow>
            </TableHead>
            <TableBody>
                {sorted.length == 0 &&
                    <TableRow>
                        <TableCell colSpan={numberOfColumns} className={styles.emptyState}>{t("DeliveriesEmptyState")}</TableCell>
                    </TableRow>
                }
                {sorted.map(delivery => (
                    <DeliveryRow key={(delivery.delivery && delivery.delivery.deliveryId) || undefined}
                        delivery={delivery}
                        occupancies={findDeliveryOccupancies(occupancies, delivery)}
                        onOpen={onOpen}
                         onCheck={onCheck}
                        checked={checked.indexOf(delivery.delivery.deliveryId!) != -1}
                    />
                ))}
            </TableBody>
        </Table>
    );
}

interface DeliveryRowProps {
    delivery: Delivery;
    occupancies: Occupancy[];
    onOpen: (delivery: Delivery) => void;
    onCheck: (delivery: Delivery, boxNumber: string) => void;
    checked: boolean;
}

function DeliveryRow({ delivery, occupancies, onOpen, onCheck, checked }: DeliveryRowProps) {

    const { language } = useLocalization();

    const id = (delivery.metadata && delivery.metadata.foreignId) ||
        (delivery.delivery && delivery.delivery.deliveryId) || "";

    const handleClick = useCallback(() => onOpen(delivery), [onOpen, delivery]);
    const handleCheck = useCallback((delivery: Delivery) => onCheck(delivery, delivery.delivery.boxNumber!), [onCheck, delivery]);

    const storedAt = formatDateTime(delivery.delivery.storedAt, language);
    const pickedUp = delivery.delivery.pickedUpAt ? formatDateTime(delivery.delivery.pickedUpAt, language) : '';

    return (
        <TableRow onClick={handleClick}>
            <TableCell column="delivery">
                <DeliveryStateDisplay deliveryState={delivery.delivery.state} />
                {delivery.tags &&
                    <DeliveryTagsDisplay tags={delivery.tags} maxTags={1} noWrap />
                }
            </TableCell>
            <TableCell>
                {delivery.order &&
                    <Fragment>
                        <div>{delivery.order.description}</div>
                        <div>{delivery.order.orderId}</div>
                    </Fragment>
                }
                <span className={typography.monospace}>
                    {shortenUuid(id)}
                </span>
            </TableCell>
            <TableCell>
                {delivery.recipients.map((recipient, index) => (
                    <div key={index}>
                        {recipient.recipientName}
                    </div>
                ))}
            </TableCell>
            <TableCell>
                <div>{storedAt}</div>
                {delivery.delivery.pickedUpAt &&
                    <div>{pickedUp}</div>
                }
            </TableCell>
            <TableCell>
                {delivery.delivery.boxNumber && delivery.delivery.state == DeliveryState.PickedUp &&
                    <Avatar size={1.5}
                            primary={!checked}
                            key={delivery.delivery.boxNumber}
                            onClick={() => handleCheck(delivery)}
                    >
                        {delivery.delivery.boxNumber}
                    </Avatar>
                }
                {occupancies.map(occupancy => (
                    <Avatar size={1.5} primary={!checked} key={occupancy.uuid}>{occupancy.boxNumber}</Avatar>
                ))}
            </TableCell>
        </TableRow>
    );
}

const styles = stylesheet({
    emptyState: {
        textAlign: 'center',
        paddingTop: '2em',
        paddingBottom: '2em',
    }
});